import { useLayoutEffect } from "react";

import { useParams } from "react-router-dom";

import useOnboardingStore from "@app/analytics/onboarding/hooks/useOnboardingStore";
import type { RouteStepId } from "@app/analytics/onboarding/types";
import { getStepIdFromRouteStepId } from "@app/analytics/onboarding/utils";
import useAuth from "@app/common/auth/useAuth";

import useOnboarding from "./hooks/useOnboarding";
import LoadingStep from "./steps/LoadingStep";
import STEP_COMPONENTS from "./steps/stepComponents";
import { StepCard } from "./steps/styles";
import {
  Container,
  LeftColumn,
  LeftColumnContent,
  RightColumn,
  RightColumnContent,
} from "./styles";
import WelcomeProgress from "./WelcomeProgress";

const StepContent = ({ routeStepId }: { routeStepId: RouteStepId | null }) => {
  const stepId = getStepIdFromRouteStepId(routeStepId);
  if (!stepId) return null;

  const Component = STEP_COMPONENTS[stepId];
  return Component ? <Component /> : null;
};

const Welcome = () => {
  const { user } = useAuth();
  const { step: routeStepId } = useParams() as { step: RouteStepId | null };

  const { isInitializing } = useOnboarding();
  const { activeStepIndex, context, reset, steps } = useOnboardingStore();

  // reset onboarding state once we've left onboarding
  useLayoutEffect(() => reset, []);

  const isLoading = isInitializing || !routeStepId || !user;

  return (
    <Container>
      <LeftColumn>
        <LeftColumnContent>
          <WelcomeProgress
            firstName={user?.firstName}
            signupSource={context.source}
            steps={steps}
            activeStepIndex={activeStepIndex}
            loading={isLoading}
          />
        </LeftColumnContent>
      </LeftColumn>
      <RightColumn>
        <RightColumnContent>
          <StepCard>
            {isLoading ? (
              <LoadingStep />
            ) : (
              <StepContent routeStepId={routeStepId} />
            )}
          </StepCard>
        </RightColumnContent>
      </RightColumn>
    </Container>
  );
};

export default Welcome;
